import React, { useId } from 'react';
import type { SwitchProps as ChakraSwitchProps } from '@chakra-ui/react';
import { Switch as ChakraSwitch, forwardRef } from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';

type SwitchSizes = 'lg' | 'sm';

export interface SwitchProps extends ChakraSwitchProps {
  /**
   * Default theme color
   * @default light
   */
  colorMode?: ColorMode;
  /**
   * The size of the switch
   * @default lg
   */
  size?: SwitchSizes;

  /**
   * If `true`, the switch will be disabled.
   * @default false
   */
  isDisabled?: boolean;

  /**
   * If `true`, the switch will be on.
   * @default false
   */
  isChecked?: boolean;
}

const defaultProps: Partial<SwitchProps> = {
  size: 'lg',
  colorMode: 'light',
  isDisabled: false,
};

/**
 * The `Switch` component is used as an alternative for the `Checkbox` component. You can switch between enabled or disabled states.
 *
 * `Switch` must always be accompanied by a label, and follows the same keyboard workflow as a `Checkbox`.
 */
export const Switch = forwardRef<SwitchProps, 'input'>((props, ref) => {
  const { colorMode: propColorMode, ...rest } = props;

  const { colorMode } = useColorMode(propColorMode);

  return (
    <ChakraSwitch
      ref={ref}
      id={useId()}
      aria-label="Switch"
      data-testid="alle-switch"
      colorScheme={colorMode}
      {...rest}
    />
  );
});

Switch.defaultProps = defaultProps;
